import { useState} from "react";
import { TextField, Popover, Box } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { DayPicker } from "react-day-picker";
import { formatDateTime } from "./DateFormat";

const calendarContainerStyles = {
  padding: "16px",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  backgroundColor: "#ffffff",

  "& .rdp-root": {
    "--rdp-accent-color": "#1976d2",
    "--rdp-accent-background-color": "#e3f2fd",
    "--rdp-day-width": "40px",
    "--rdp-day-height": "40px",
    "--rdp-day_button-width": "40px",
    "--rdp-day_button-height": "40px",
    margin: 0,
  },

  "& .rdp-months": {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
    justifyContent: "center",
  },

  "& .rdp-month": {
    margin: 0,
  },

  "& .rdp-month_caption": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 0",
    marginBottom: "12px",
  },

  "& .rdp-caption_label": {
    fontSize: "16px",
    fontWeight: 600,
    color: "#1a1a1a",
    textTransform: "capitalize",
  },

  "& .rdp-nav": {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    position: "absolute",
    top: "16px",
    width: "calc(100% - 32px)",
    justifyContent: "space-between",
    padding: "0 8px",
    zIndex: 1,
  },

  "& .rdp-button_previous, & .rdp-button_next": {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    padding: 0,
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    color: "#666666",
    transition: "all 0.2s ease",

    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#1976d2",
      color: "#1976d2",
    },

    "&:disabled": {
      opacity: 0.4,
      cursor: "not-allowed",
    },
  },

  "& .rdp-chevron": {
    width: "16px",
    height: "16px",
    fill: "currentColor",
  },

  "& .rdp-weekdays": {
    display: "flex",
    borderBottom: "1px solid #f0f0f0",
    marginBottom: "8px",
    paddingBottom: "8px",
  },

  "& .rdp-weekday": {
    width: "40px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 600,
    color: "#888888",
    textTransform: "uppercase",
  },

  "& .rdp-weeks": {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  "& .rdp-week": {
    display: "flex",
  },

  "& .rdp-day": {
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },

  "& .rdp-day_button": {
    width: "36px",
    height: "36px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: 400,
    color: "#1a1a1a",
    border: "none",
    borderRadius: "50%",
    backgroundColor: "transparent",
    cursor: "pointer",
    transition: "all 0.15s ease",
    position: "relative",
    zIndex: 1,
    userSelect: "none",

    "&:hover": {
      backgroundColor: "#e3f2fd",
      color: "#1976d2",
    },
  },

  "& .rdp-today .rdp-day_button": {
    fontWeight: 700,
    color: "#1976d2",
    border: "2px solid #1976d2",
  },

  "& .rdp-selected .rdp-day_button": {
    backgroundColor: "#1976d2",
    color: "#ffffff",
    fontWeight: 500,

    "&:hover": {
      backgroundColor: "#1565c0",
      color: "#ffffff",
    },
  },

  "& .rdp-range_start": {
    position: "relative",

    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      right: 0,
      transform: "translateY(-50%)",
      width: "50%",
      height: "36px",
      backgroundColor: "#e3f2fd",
      zIndex: 0,
    },
  },

  "& .rdp-range_start .rdp-day_button": {
    backgroundColor: "#1976d2",
    color: "#ffffff",
    borderRadius: "50%",
    zIndex: 1,
  },

  "& .rdp-range_end": {
    position: "relative",

    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: 0,
      transform: "translateY(-50%)",
      width: "50%",
      height: "36px",
      backgroundColor: "#e3f2fd",
      zIndex: 0,
    },
  },

  "& .rdp-range_end .rdp-day_button": {
    backgroundColor: "#1976d2",
    color: "#ffffff",
    borderRadius: "50%",
    zIndex: 1,
  },

  "& .rdp-range_start.rdp-range_end": {
    "&::before": {
      display: "none",
    },
  },

  "& .rdp-range_middle": {
    position: "relative",

    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: 0,
      right: 0,
      transform: "translateY(-50%)",
      height: "36px",
      backgroundColor: "#e3f2fd",
      zIndex: 0,
    },
  },

  "& .rdp-range_middle .rdp-day_button": {
    backgroundColor: "transparent",
    color: "#1565c0",
    borderRadius: 0,
    zIndex: 1,

    "&:hover": {
      backgroundColor: "#bbdefb",
    },
  },

  "& .rdp-disabled .rdp-day_button": {
    color: "#bdbdbd",
    cursor: "not-allowed",
    opacity: 0.5,

    "&:hover": {
      backgroundColor: "transparent",
      color: "#bdbdbd",
    },
  },

  "& .rdp-outside .rdp-day_button": {
    color: "#c0c0c0",
    opacity: 0.6,
  },

  "& .rdp-hidden": {
    visibility: "hidden",
  },
};

const DateRangePicker = ({
  selectedRange,
  onRangeChange,
  placeholder = "Select date range",
  numberOfMonths = 2,
  minDate,
  maxDate,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempRange, setTempRange] = useState(undefined);
  const [selectionStep, setSelectionStep] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(null);

  const open = Boolean(anchorEl);

  const getDefaultMonth = () => {
    if (currentMonth) {
      return currentMonth;
    }

    if (selectedRange?.from) {
      const fromDate = new Date(selectedRange.from);
      if (numberOfMonths === 2 && selectedRange?.to) {
        const toDate = new Date(selectedRange.to);
        const monthDiff = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + 
                         (toDate.getMonth() - fromDate.getMonth());
        
        if (monthDiff >= 1) {
          return new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
        }
        return new Date(toDate.getFullYear(), toDate.getMonth() - 1, 1);
      }
      return new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
    }

    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - 1, 1);
  };

  const defaultMonth = getDefaultMonth();

  const handleOpen = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setTempRange(selectedRange?.from && selectedRange?.to ? selectedRange : undefined);
      setSelectionStep(1);
      setCurrentMonth(null);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTempRange(undefined);
    setSelectionStep(1);
    setCurrentMonth(null);
  };

  const handleMonthChange = (month) => {
    setCurrentMonth(month);
  };

  const handleSelect = (range, selectedDay) => {
    if (selectionStep === 1) {
      const startDate = new Date(selectedDay);
      startDate.setHours(0, 0, 0, 0);
      setTempRange({ from: startDate, to: undefined });
      setSelectionStep(2);
    } else {
      const startDate = tempRange?.from;

      if (startDate) {
        let finalRange;
        const endDate = new Date(selectedDay);
        endDate.setHours(0, 0, 0, 0);
        
        if (endDate < startDate) {
          finalRange = {
            from: endDate,
            to: startDate,
          };
        } else {
          finalRange = {
            from: startDate,
            to: endDate,
          };
        }

        setTempRange(finalRange);
        onRangeChange(finalRange);
        handleClose();
      }
    }
  };

  const displayValue =
    selectedRange?.from && selectedRange?.to
      ? `${formatDateTime(selectedRange.from)} - ${formatDateTime(selectedRange.to)}`
      : "";

  return (
    <>
      <TextField
        fullWidth
        value={displayValue}
        placeholder={placeholder}
        onClick={handleOpen}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <CalendarMonthIcon
              fontSize="small"
              sx={{
                color: disabled ? "#bdbdbd" : "#666666",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            />
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: 44,
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: disabled ? "#f5f5f5" : "#ffffff",
            borderRadius: "8px",
            transition: "all 0.2s ease",

            "&:hover": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: disabled ? "#e0e0e0" : "#1976d2",
              },
            },

            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1976d2",
                borderWidth: "2px",
              },
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e0e0e0",
          },
          "& input": {
            padding: "10.5px 14px",
            cursor: disabled ? "not-allowed" : "pointer",
            fontSize: "14px",
            color: "#1a1a1a",

            "&::placeholder": {
              color: "#999999",
              opacity: 1,
            },
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              border: "1px solid #e8e8e8",
              marginTop: "8px",
              overflow: "visible",
            },
          },
        }}
      >
        <Box sx={calendarContainerStyles}>
          <DayPicker
            mode="range"
            selected={tempRange}
            onSelect={handleSelect}
            numberOfMonths={numberOfMonths}
            defaultMonth={defaultMonth}
            month={currentMonth}
            onMonthChange={handleMonthChange}
            disabled={maxDate ? { after: maxDate } : undefined}
            fromDate={minDate}
            toDate={maxDate}
            fixedWeeks
          />
        </Box>
      </Popover>
    </>
  );
};

export default DateRangePicker;